import isEqual from 'lodash/isEqual'

import * as Sentry from '@sentry/react'

import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'

import type { Service } from '../graphqlEnums'
import type { EditPostInput } from './graphqlTypes'
import { mapToEditPostInput } from './mapToEditPostInput'
import type { MapperContext } from './types'

/**
 * Diffs the payload about to ship against the legacy mapper's output for the
 * same draft. `ctx` must NOT carry `channelVariant` — the base context makes
 * every migrated mapper take its Flux-only branch, the actual legacy
 * behavior being compared against. Only field names are logged/reported,
 * never post content.
 */
/**
 * Reports a failure to even build the comparison payload — a stronger
 * signal than a field-level drift, since it means the still-unramped path
 * would have thrown for this channel. Not sampled (unlike the drift check
 * below): a build failure should be rare enough that every occurrence is
 * worth seeing.
 */
export function reportEditPostPayloadBuildFailure(
  channelId: string,
  service: Service,
  error: unknown,
): void {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error(
      '[composer.bridge] edit-post payload build failed',
      `channelId=${channelId}`,
      `service=${service}`,
      error,
    )
    return
  }

  Sentry.captureException(error, {
    fingerprint: [
      'composer-bridge-drift',
      'payload-edit-build-failed',
      service,
    ],
    tags: {
      track: 'payload-edit',
      kind: 'buildFailed',
      service,
    },
    extra: { channelId },
  })
}

export function checkEditPostPayloadDrift(
  draft: Draft,
  ctx: MapperContext,
  channelId: string,
  service: Service,
  postId: string,
  newInput: EditPostInput,
): void {
  const isProduction = process.env.NODE_ENV === 'production'
  // 0.04 local sample x 0.25 Sentry client sampleRate (PublishSentryClient
  // applies it to every captured event) = ~1% of drift occurrences reported.
  // Keep in sync with createPostPayloadDrift/omniDrift/channelDrift's rate.
  if (isProduction && Math.random() > 0.04) return

  const legacyInput = mapToEditPostInput(draft, ctx, postId)

  const driftedFields: string[] = []
  if (!isEqual(legacyInput.text, newInput.text)) driftedFields.push('text')
  if (!isEqual(legacyInput.schedulingType, newInput.schedulingType))
    driftedFields.push('schedulingType')
  if (!isEqual(legacyInput.assets, newInput.assets))
    driftedFields.push('assets')
  if (!isEqual(legacyInput.tagIds, newInput.tagIds))
    driftedFields.push('tagIds')
  if (!isEqual(legacyInput.aiAssisted, newInput.aiAssisted))
    driftedFields.push('aiAssisted')
  if (!isEqual(legacyInput.metadata, newInput.metadata))
    driftedFields.push('metadata')
  if (!isEqual(legacyInput.mode, newInput.mode)) driftedFields.push('mode')
  if (!isEqual(legacyInput.dueAt, newInput.dueAt)) driftedFields.push('dueAt')
  if (!isEqual(legacyInput.saveToDraft, newInput.saveToDraft))
    driftedFields.push('saveToDraft')
  if (!isEqual(legacyInput.ideaId, newInput.ideaId))
    driftedFields.push('ideaId')
  if (!isEqual(legacyInput.draftId, newInput.draftId))
    driftedFields.push('draftId')
  if (!isEqual(legacyInput.source, newInput.source))
    driftedFields.push('source')
  if (!isEqual(legacyInput.approvalChange, newInput.approvalChange))
    driftedFields.push('approvalChange')

  if (driftedFields.length === 0) return

  if (!isProduction) {
    // eslint-disable-next-line no-console
    console.error(
      '[composer.bridge] edit-post payload drift detected',
      `channelId=${channelId}`,
      `service=${service}`,
      `fields=${driftedFields.join(',')}`,
    )
    return
  }

  const composerMode = AppStore.getOptions().composerMode ?? 'unknown'

  Sentry.addBreadcrumb({
    category: 'composer.bridge',
    level: 'warning',
    message: 'edit-post payload drift',
    data: {
      track: 'payload-edit',
      service,
      channelId,
      fields: driftedFields,
      composerMode,
    },
  })
  Sentry.captureEvent({
    message: 'composer.bridge.drift',
    level: 'warning',
    fingerprint: ['composer-bridge-drift', 'payload-edit', service],
    tags: {
      track: 'payload-edit',
      service,
      fields: driftedFields.join(','),
      composerMode,
    },
    extra: {
      channelId,
      driftedFieldCount: driftedFields.length,
    },
  })
}
